import {
  PricingCustomerDetailHeader,
  PricingCustomerDetailRow,
} from '@sales-portal/domain/pricing/screens/customers/blocs/transformers/use-customer-detail-transformer';

enum CustomerDetailWeightedField {
  CostDeltaInMicros = 'costDeltaInMicros',
  CostInMicros = 'costInMicros',
  GrossMarginDeltaInMicros = 'grossMarginDeltaInMicros',
  GrossMarginPercentage = 'grossMarginPercentage',
  GrossMarginPercentageDelta = 'grossMarginPercentageDelta',
  GrossProfitPerUnitInMicros = 'grossProfitPerUnitInMicros',
  PriceDeltaInMicros = 'priceDeltaInMicros',
  PriceInMicros = 'priceInMicros',
}
const WEIGHTED_FIELDS = Object.values(CustomerDetailWeightedField);

export const buildCustomerDetailAggregateRow = (
  header: PricingCustomerDetailHeader,
  rows: PricingCustomerDetailRow[],
): PricingCustomerDetailRow => {
  // Per-field weighted average: null values are excluded from BOTH the
  // numerator AND denominator so rows missing data don't bias the average
  // toward 0. If no row has data for a field, the aggregate value is null
  // and the column renders "--".
  const weightedAverages = WEIGHTED_FIELDS.reduce<
    Record<CustomerDetailWeightedField, number | null>
  >(
    (acc, field) => {
      let weightedSum = 0;
      let weightSum = 0;
      for (const row of rows) {
        const value = row[field];
        if (value === null) {
          continue;
        }
        weightedSum += value * row.lineItems;
        weightSum += row.lineItems;
      }
      acc[field] = weightSum > 0 ? weightedSum / weightSum : null;
      return acc;
    },
    {} as Record<CustomerDetailWeightedField, number | null>,
  );

  // Impacts are sums, not weighted averages. grossProfitImpactInMicros keeps
  // null when no row has data; editsGrossProfitImpactInMicros is always defined
  // (0 when there are no edits).
  let grossProfitImpactInMicros: number | null = null;
  let editsGrossProfitImpactInMicros = 0;
  for (const row of rows) {
    if (row.grossProfitImpactInMicros !== null) {
      grossProfitImpactInMicros =
        (grossProfitImpactInMicros ?? 0) + row.grossProfitImpactInMicros;
    }
    editsGrossProfitImpactInMicros += row.editsGrossProfitImpactInMicros;
  }

  return {
    ...weightedAverages,
    editsGrossProfitImpactInMicros,
    grossProfitImpactInMicros,
    invoiceHistory: [],
    lastCostInMicros: null,
    lastMarginPercentage: null,
    lastPriceInMicros: null,
    lineItems: header.lineItems,
    restaurantUUID: header.restaurantUUID,
    supplierDisplayInfo: {},
    supplierUUID: rows[0]?.supplierUUID ?? '',
    variantPackId: header.restaurantAccountId,
    variantPackName: header.restaurantName,
    variantPackUOM: '',
    variantPackUUID: header.restaurantUUID,
    weeklyPurchaseQuantities: [],
  };
};
